import { ManagedServiceId } from '@axinom/mosaic-id-utils';
import {
  type Column,
  createDateRangeFilterValidators,
  DateRenderer,
  EmptyStation,
  type ExplorerDataProvider,
  filterToPostGraphileFilter,
  FilterTypes,
  MessageBar,
  NavigationExplorer,
  sortToPostGraphileOrderBy,
  transformRange,
} from '@axinom/mosaic-ui';
import { config } from 'config';
import type { BreadcrumbResolver } from 'models/breadcrumb';
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  type IdpUserStoreFilter,
  IdpUserStoresDocument,
  IdpUserStoresOrderBy,
  type IdpUserStoresQuery,
  type IdpUserStoresQueryVariables,
  UserStoreCategory,
} from '../../../../generated/axauth-service';
import { getManagedServiceAvailabilityMessage } from '../../../../util/managedServiceUtil';
import { environmentApolloClient } from '../../../environmentApolloClient';

type IdpUserStoreData = NonNullable<
  IdpUserStoresQuery['filtered']
>['nodes'][number];

export const getUserStoreCategoryText = (userStoreCategory: string): string => {
  switch (userStoreCategory) {
    case UserStoreCategory.Management:
      return 'Management';
    case UserStoreCategory.EndUser:
      return 'End-User';
    default:
      return userStoreCategory;
  }
};

const CategoryRenderer = (value: unknown): JSX.Element => (
  <>{getUserStoreCategoryText(value as string)}</>
);

const explorerColumns: Column<IdpUserStoreData>[] = [
  { label: 'Name', propertyName: 'name' },
  { label: 'Category', propertyName: 'category', render: CategoryRenderer },
  { label: 'Created At', propertyName: 'createdAt', render: DateRenderer },
  {
    label: 'Last Modified At',
    propertyName: 'updatedAt',
    render: DateRenderer,
  },
];

export const UserStores: React.FC = () => {
  const { envId } = useParams<{
    envId: string;
  }>();

  const [serviceStatusMessage, setServiceStatusMessage] = useState<
    string | null
  >(null);

  // Check the status of axauth-service
  useEffect(() => {
    (async () => {
      const statusMessage = await getManagedServiceAvailabilityMessage(
        ManagedServiceId.AXAUTH_SERVICE,
        envId,
      );
      setServiceStatusMessage(statusMessage);
    })();
  }, [envId]);

  const dataProvider: ExplorerDataProvider<IdpUserStoreData> = {
    loadData: async ({ pagingInformation, sorting, filters }) => {
      const envApolloClient = environmentApolloClient(
        envId,
        new URL(`/graphql`, config.axAuthAdminBaseUrl).toString(),
      );

      const idpUserStores = await envApolloClient?.query<
        IdpUserStoresQuery,
        IdpUserStoresQueryVariables
      >({
        query: IdpUserStoresDocument,
        variables: {
          filter: filterToPostGraphileFilter<IdpUserStoreFilter>(filters, {
            name: 'includesInsensitive',
            category: 'equalTo',
            createdAt: transformRange,
            updatedAt: transformRange,
          }),
          orderBy: sortToPostGraphileOrderBy(sorting, IdpUserStoresOrderBy),
          after: pagingInformation,
        },
        fetchPolicy: 'network-only',
      });

      const data = idpUserStores?.data.filtered?.nodes ?? [];

      return {
        data,
        totalCount: idpUserStores?.data.nonFiltered?.totalCount ?? 0,
        filteredCount: idpUserStores?.data.filtered?.totalCount ?? 0,
        hasMoreData:
          idpUserStores?.data.filtered?.pageInfo.hasNextPage ?? false,
        pagingInformation: idpUserStores?.data.filtered?.pageInfo.endCursor,
      };
    },
  };

  const [createFromDateFilterValidator, createToDateFilterValidator] =
    createDateRangeFilterValidators<IdpUserStoreData>();

  return (
    <>
      {serviceStatusMessage ? (
        <EmptyStation title="User Stores">
          <MessageBar type="warning" title={serviceStatusMessage}></MessageBar>
        </EmptyStation>
      ) : (
        <NavigationExplorer<IdpUserStoreData>
          title="User Stores"
          stationKey="UserStoresExplorer"
          columns={explorerColumns}
          dataProvider={dataProvider}
          calculateNavigateUrl={(item) => {
            return `/env/${envId}/services/${ManagedServiceId.AXAUTH_SERVICE}/config/userStores/${item.id}`;
          }}
          onCreateAction={`/env/${envId}/services/${ManagedServiceId.AXAUTH_SERVICE}/config/userStores/create`}
          defaultSortOrder={{ column: 'updatedAt', direction: 'desc' }}
          filterOptions={[
            {
              label: 'Name',
              property: 'name',
              type: FilterTypes.FreeText,
            },
            {
              label: 'Category',
              property: 'category',
              type: FilterTypes.Options,
              options: [
                {
                  value: UserStoreCategory.Management,
                  label: 'Management',
                },
                {
                  value: UserStoreCategory.EndUser,
                  label: 'End-User',
                },
              ],
            },
            {
              label: 'Creation Period (From)',
              property: 'createdAt',
              type: FilterTypes.Date,
              onValidate: createFromDateFilterValidator('createdAt'),
            },
            {
              label: 'Creation Period (To)',
              property: 'createdAt',
              type: FilterTypes.Date,
              onValidate: createToDateFilterValidator('createdAt'),
            },
            {
              label: 'Modification Period (From)',
              property: 'updatedAt',
              type: FilterTypes.Date,
              onValidate: createFromDateFilterValidator('updatedAt'),
            },
            {
              label: 'Modification Period (To)',
              property: 'updatedAt',
              type: FilterTypes.Date,
              onValidate: createToDateFilterValidator('updatedAt'),
            },
          ]}
        />
      )}
    </>
  );
};

export const UserStoresCrumb: BreadcrumbResolver = () => 'User Stores';
